import type { EyemedEligibilityTab, EyemedTab, EyemedTable, EyemedTabsBenefits, EyemedTabState } from '../types'
import { eyemedTabsBenefitsAreEmpty } from '../types'

/**
 * What the EyeMed panel shows for a stored check in the tabs shape — the pure
 * half of `EyemedSlideout`'s tabs body, so the copy and the section list are
 * tested without the DOM.
 *
 * The panel mirrors the member's benefits page: one section per tab the
 * portal listed, in the portal's order, each in one of three states. The one
 * thing added that the portal did not print is a Medical section when no
 * Medical tab was listed: the question the feature exists to answer ("does
 * this patient have medical benefits?") gets an answer on every result.
 */

/** The tab whose section is always shown. Matched by name as printed,
 *  case-insensitively, so a portal that prints "MEDICAL" still gets one
 *  section and not two. */
export const MEDICAL_TAB = 'Medical'

export function isMedicalTab(name: string): boolean {
  return name.trim().toLowerCase() === MEDICAL_TAB.toLowerCase()
}

/**
 * The sentence a section shows in place of a body when its tab was read with
 * no rows (`empty`) or could not be read (`failed`). The Medical sentences are
 * the spec's literals; every other tab is named as the portal printed it
 * ("No LASIK benefits on this EyeMed plan.").
 */
export function tabSentence(name: string, state: Exclude<EyemedTabState, 'rows'>): string {
  if (isMedicalTab(name)) {
    return state === 'empty'
      ? 'No medical benefits on this EyeMed plan.'
      : 'Medical benefits could not be read. Try the check again.'
  }
  return state === 'empty'
    ? `No ${name} benefits on this EyeMed plan.`
    : `${name} benefits could not be read. Try the check again.`
}

/**
 * The sections the panel renders: the stored tabs in delivery order, plus a
 * Medical section in the `empty` state appended LAST when the portal listed
 * no Medical tab — the portal's order is kept for what it printed, and a
 * section it never printed takes no position inside that order. A listed
 * Medical tab, in any state, is the Medical section; no second one is added.
 *
 * A delivery the ingest read as empty — no tabs, or every tab read with no
 * rows (`eyemedTabsBenefitsAreEmpty`, the ingest's own rule) — is the
 * member-not-found doc, not a result. It still reaches the panel from a
 * Cockpit needs-review row, the Recovery Loop's "View check" and a
 * permalink, and a Medical sentence there would state a plan fact about a
 * member the portal never found. Such a delivery renders no section at all,
 * as the legacy map's empty body does today.
 */
export function panelSections(benefits: EyemedTabsBenefits): EyemedTab[] {
  if (eyemedTabsBenefitsAreEmpty(benefits)) return []
  const sections = [...benefits.tabs]
  if (!sections.some(tab => isMedicalTab(tab.name))) {
    sections.push({ name: MEDICAL_TAB, state: 'empty', tables: [] })
  }
  return sections
}

/** A balances table ("Declining Balance Package / Starting Balance /
 *  Remaining Balance") — shown at the top of its tab's section, every column
 *  kept, never folded onto a benefit row. */
export function isBalancesTable(table: EyemedTable): boolean {
  return table.source === 'balances'
}

/**
 * The tables a section shows, every one of them: a balances table first, at
 * the top of the section, kept whole (header row and every column) — a
 * declining balance belongs to whichever allowance the plan funds, and no
 * document guarantees the package name matches a row label, so it is shown
 * as printed rather than folded onto a benefit row — then the benefit tables
 * in page order. A table with no tag was stored before tables were tagged
 * and is a benefit table. Shown for every stored check carrying one,
 * whenever it was stored.
 */
export function panelTables(tab: EyemedTab): EyemedTable[] {
  return [...tab.tables.filter(isBalancesTable), ...tab.tables.filter(t => !isBalancesTable(t))]
}

/** The benefit rows a section shows — the count beside its title. A balances
 *  table's rows are not benefit rows and are not counted. */
export function panelRowCount(tab: EyemedTab): number {
  return tab.tables.filter(t => !isBalancesTable(t)).reduce(
    (n, table) => n + table.groups.reduce((m, group) => m + group.rows.length, 0),
    0,
  )
}

// ── The Eligibility block above the tabs ─────────────────────────────────

/** The sentence the Eligibility block shows for a table recorded as failed. */
export const ELIGIBILITY_COULD_NOT_READ = 'Eligibility could not be read. Try the check again.'

/**
 * What the Eligibility block renders: `none` — no block at all (every check
 * stored before the blocks, the bot, the demo), or a delivery the ingest read
 * as empty (the member-not-found doc, whose panel must claim nothing about a
 * member the portal never found); `failed` — the table could not be read;
 * `empty` — the shown table read with no rows (or no tabs at all); else the
 * answer's tabs as stored, the shown one first, each a named table to
 * render in that order ("Routine", then "Additional Purchase"). The block
 * never computes a verdict: the all-No answer is the check's classification,
 * shown by the Cockpit row, never a sentence here — and it reads the shown
 * table only.
 */
export type EligibilityBlockView =
  | 'none'
  | 'failed'
  | 'empty'
  | { tabs: EyemedEligibilityTab[] }

export function eligibilityBlock(benefits: EyemedTabsBenefits): EligibilityBlockView {
  if (eyemedTabsBenefitsAreEmpty(benefits)) return 'none'
  const block = benefits.eligibility
  if (!block) return 'none'
  if (block.state === 'failed') return 'failed'
  const shown = block.tabs[0]
  if (!shown || shown.rows.length === 0) return 'empty'
  return { tabs: block.tabs }
}

/** The four columns' captions, read off the stored header the way the
 *  scraper mapped the columns (by normalized text: "service" — not "service
 *  frequency" — "member is eligible", "eligible as of", "frequency"; the
 *  portal's header also carries a hidden sort column and a blank checkbox
 *  column, which caption nothing), with the portal's wording as the
 *  fallback for a header that lacks one. */
export interface EligibilityCaptions {
  service: string
  eligible: string
  eligibleAsOf: string
  frequency: string
}

export const DEFAULT_ELIGIBILITY_CAPTIONS: EligibilityCaptions = {
  service: 'Service',
  eligible: 'Member is Eligible?',
  eligibleAsOf: 'Member Eligible As Of',
  frequency: 'Service Frequency',
}

export function eligibilityCaptions(header: string[]): EligibilityCaptions {
  const cells = header.map(c => c.trim())
  const lower = cells.map(c => c.toLowerCase().replace(/\s+/g, ' '))
  const find = (needle: string) => {
    const i = lower.findIndex(c => c.includes(needle))
    return i === -1 ? undefined : cells[i]
  }
  const frequencyIndex = lower.findIndex(c => c.includes('frequency'))
  const serviceIndex = lower.findIndex((c, i) => c.startsWith('service') && i !== frequencyIndex)
  return {
    service: (serviceIndex === -1 ? undefined : cells[serviceIndex]) || DEFAULT_ELIGIBILITY_CAPTIONS.service,
    eligible: find('member is eligible') || DEFAULT_ELIGIBILITY_CAPTIONS.eligible,
    eligibleAsOf: find('eligible as of') || DEFAULT_ELIGIBILITY_CAPTIONS.eligibleAsOf,
    frequency: find('frequency') || DEFAULT_ELIGIBILITY_CAPTIONS.frequency,
  }
}
